import PropTypes from 'prop-types';
import Layout from 'layouts/Default';
import { Section } from 'elements/Containers';
import PostCard from 'components/PostCard';
import Pagination from 'components/Pagination';
import BlogCta from 'components/BlogCta';
import Yoast from 'components/Yoast';
import { Caption, Heading2 } from 'elements/Typography';

import {
  getPaginatedPostsByCategory,
  getPostCountByCategory,
  getAllCategoriesWithSlug,
} from 'lib/api';
import { contentParser } from 'lib/parser';
import Canonical from 'components/Canonical';

const Category = ({ posts: { edges }, category, data: { pages }, slug }) => {
  const { content } = contentParser(category.description || '');

  return (
    <Layout>
      <Canonical />
      <Yoast seo={category.seo} />
      <Section>
        <div className="text-center md:mx-auto md:max-w-2xl">
          <Caption as="p">Archive</Caption>
          <Heading2 as="h1" className="text-grey-10">
            {category.name}
          </Heading2>
          {content && (
            <div className="prose prose-lg mx-auto mt-6">{content}</div>
          )}
        </div>
        <div className="mx-auto mt-12 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
          {edges.map(({ node }, index) => (
            <PostCard
              key={node.id}
              id={node.id}
              slug={node.slug}
              featuredImage={node.featuredImage}
              categories={node.categories}
              title={node.title}
              author={node.author}
              date={node.date}
              priority={index <= 2}
            />
          ))}
        </div>
        <Pagination
          currentPage={1}
          totalPages={pages}
          base={`/category/${slug}`}
        />
        <BlogCta />
      </Section>
    </Layout>
  );
};

Category.propTypes = {
  posts: PropTypes.shape([]).isRequired,
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    seo: PropTypes.shape({}).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    pages: PropTypes.number.isRequired,
  }).isRequired,
  slug: PropTypes.string.isRequired,
};

export async function getStaticPaths() {
  const allCategories = await getAllCategoriesWithSlug();

  return {
    paths:
      allCategories.edges.map(({ node }) => `/category/${node.slug}`) || [],
    fallback: 'blocking',
  };
}

export const getStaticProps = async ({ params }) => {
  const { posts, category } = await getPaginatedPostsByCategory(params.slug);
  const data = await getPostCountByCategory(params.slug);

  if (posts.edges.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      posts,
      category,
      data,
      slug: params.slug,
    },
    revalidate: 900,
  };
};

export default Category;
